import React, { useEffect, useState } from 'react';
import { Avatar } from 'antd';
import { Users, Coins, Plus, X } from 'lucide-react';
import { ICreator } from '../../types/creator';
import { useFollow } from '../../hooks/useFollow/useFollow';
import {
  formatCurrencyShortend,
  formatFollowers,
} from '../../utils/convertData.util';

const FALLBACK_AVATAR = '/default-avatar.svg';

export interface ICreatorProps {
  creator: ICreator;
  onSuccess?: () => void;
  onClick?: () => void;
  removeSuggestion?: (userId: string) => void;
}

const Creator: React.FC<ICreatorProps> = ({
  creator,
  onSuccess,
  onClick,
  removeSuggestion,
}) => {
  const [avatarSrc, setAvatarSrc] = useState<string>(
    creator.avatar || FALLBACK_AVATAR
  );
  const [coverError, setCoverError] = useState<boolean>(false);
  const { handleFollow, handleUnFollow } = useFollow();

  useEffect(() => {
    setAvatarSrc(creator.avatar || FALLBACK_AVATAR);
  }, [creator.avatar]);

  useEffect(() => {
    setCoverError(false);
  }, [creator.coverImage]);

  const handleFollowAction = async () => {
    const actionFn = creator.isFollow ? handleUnFollow : handleFollow;
    await actionFn(creator.userId);
    onSuccess?.();
  };

  return (
    <div className="ui-card !rounded-[20px] p-4 flex flex-col gap-3 tracking-[-0.02em] shadow-[4px_4px_44px_rgba(0,0,0,0.06)] dark:shadow-none transition-transform duration-200 hover:-translate-y-0.5">
      {/* Cover */}
      <div className="relative w-full aspect-[328/214] rounded-2xl overflow-hidden ui-surface-2">
        {creator.coverImage && !coverError ? (
          <img
            src={creator.coverImage}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setCoverError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-10 h-10 text-slate-400 dark:text-slate-500" />
          </div>
        )}
        {removeSuggestion && (
          <button
            type="button"
            aria-label="Ẩn gợi ý"
            onClick={() => removeSuggestion(creator.userId)}
            className="absolute top-3 right-3 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex items-center gap-2.5 min-w-0 cursor-pointer"
            onClick={onClick}
          >
            <Avatar
              alt={creator.username}
              src={avatarSrc}
              size={40}
              className="shrink-0 border border-white dark:border-slate-700"
              onError={() => {
                setAvatarSrc(FALLBACK_AVATAR);
                return true;
              }}
            />
            <span className="ui-muted text-base truncate">
              {creator.username}
            </span>
          </div>
          <span
            className="ui-heading text-lg font-semibold truncate cursor-pointer shrink-0 max-w-[45%]"
            onClick={onClick}
          >
            {creator.fullname}
          </span>
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 ui-muted">
              <Users className="w-5 h-5 shrink-0" strokeWidth={1.8} />
              <span className="text-base">Người theo dõi</span>
            </div>
            <span className="ui-heading text-base font-semibold">
              {formatFollowers(creator.totalFollow)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 ui-muted">
              <Coins className="w-5 h-5 shrink-0" strokeWidth={1.8} />
              <span className="text-base">Donate</span>
            </div>
            <span className="ui-heading text-base font-semibold">
              {formatCurrencyShortend(creator.totalDonated)}
            </span>
          </div>
        </div>
      </div>

      {/* Follow / Unfollow */}
      {creator.isFollow ? (
        <button
          type="button"
          onClick={handleFollowAction}
          className="w-full flex items-center justify-center gap-2 rounded-full py-2 text-base font-medium border border-[#DEE0E6] dark:border-slate-600 ui-text hover:bg-black/[0.03] dark:hover:bg-white/[0.06] transition-colors"
        >
          Bỏ theo dõi
        </button>
      ) : (
        <button
          type="button"
          onClick={handleFollowAction}
          className="w-full flex items-center justify-center gap-2 rounded-full py-2 text-base font-medium text-white bg-[#066bff] hover:bg-[#0559d6] transition-colors"
        >
          <Plus className="w-5 h-5" strokeWidth={2.2} />
          Theo dõi
        </button>
      )}
    </div>
  );
};

export default Creator;
